// Wraps the shared `useOptroImportJobs` tracker (hooks/useOptroImportJobs.ts)
// for the Work Step Optro import wizard, pointed at the /work-steps/external
// endpoints. The registry listing and the status endpoint return the same
// import item shape the controls import uses, so the job/item schemas are
// reused as-is from models/audit/optroSync.ts.
import { createContext, ReactNode, useContext } from "react";
import {
  OptroImportTracker,
  useOptroImportJobs,
} from "../hooks/useOptroImportJobs";
import { useOptroImportAvailable } from "../hooks/useOptroImportAvailable";
import { useWorkStepsEnabled } from "../hooks/useWorkStepsEnabled";
import { hasPermission } from "../lib/permissions";
import { OptroWorkpaperFile } from "../models/audit/optroSync";
import {
  OptroWorkStepSummary,
  OptroWorkStepSummarySchema,
  WorkStepOptroImportStartItem,
} from "../models/audit/workStep";
import { useAuth } from "./authContext";

// The wire still carries per-document `doc_type`, but the review step now
// offers ONE workpaper column instead of the old walkthrough/supplementary
// split, so every choice submits the same value. "walkthrough" is the one to
// send: it is the arm the agent's prompt names for the doc it reads to draft
// a work step, and it is what every already-enqueued payload carries. Nothing
// on the import path branches on it today — `acquire_context_docs` persists
// through `DocumentPreparer` and the only read of `choice.doc_type`
// (`work_step_optro_import.py`) sits in a block that module marks unreachable
// — so this is a wire-compatibility choice, not a behavioural one.
const CONTEXT_DOC_TYPE = "walkthrough";

export type WorkStepOptroImportContextValue = OptroImportTracker<
  WorkStepOptroImportStartItem,
  OptroWorkStepSummary
>;

const WorkStepOptroImportContext =
  createContext<WorkStepOptroImportContextValue | null>(null);

// Display label for a work step item, both as the payload's `work_step_name`
// and (prefixed with its ref) the progress panel's row label.
function workStepDisplayName(item: WorkStepOptroImportStartItem): string {
  return `${item.workStep.externalRef} - ${item.workStep.name}`;
}

// One `context_docs[]` entry of the import request.
function contextDocEntry(file: OptroWorkpaperFile) {
  return {
    doc_type: CONTEXT_DOC_TYPE,
    kind: file.source === "optro" ? "optro_attachment" : "upload",
    // An upload carries the presigned upload id in `id`; a picked Optro file
    // carries a candidate id ("att-…"/"file-…") that means nothing to the
    // backend, which downloads by `file_id`.
    upload_id: file.source === "upload" ? file.id : null,
    file_id: file.fileId,
    file_name: file.fileName,
  };
}

// One `items[]` entry of the import request. Under `all_optro` the backend
// lists and downloads the item's Optro workpapers itself and `context_docs`
// carries only the user's uploads, added on top; under `explicit` it carries
// exactly the files checked in the review dialog.
function importItemBody(item: WorkStepOptroImportStartItem) {
  const { workStep, decision } = item;
  const contextDocs =
    decision.mode === "all_optro" ? decision.uploads : decision.files;
  return {
    external_ref: workStep.externalRef,
    ops_audit_item_id: workStep.opsAuditItemId,
    work_step_name: workStep.name,
    context_docs_mode: decision.mode,
    context_docs: contextDocs.map(contextDocEntry),
  };
}

export function WorkStepOptroImportProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { workspaceMember } = useAuth();
  const auditAvailable = useOptroImportAvailable();
  const workStepsEnabled = useWorkStepsEnabled();
  const tracker = useOptroImportJobs<
    WorkStepOptroImportStartItem,
    OptroWorkStepSummary
  >({
    kind: "work_step",
    // The work-step listing needs the work-steps flag and edit_workstep, and
    // the user must be able to reach the audit surface at all.
    enabled:
      auditAvailable &&
      workStepsEnabled &&
      hasPermission(workspaceMember, "edit_workstep"),
    basePath: "/work-steps/external",
    noun: "work steps",
    toRequestItem: (item) => ({
      ...importItemBody(item),
      // Description blanked: it can be long, and placeholders don't show it.
      display: { ...item.workStep, description: "" },
    }),
    toOptimisticItem: (item) => ({
      controlExternalRef: item.workStep.externalRef,
      controlName: workStepDisplayName(item),
      status: "queued",
    }),
    summaryOf: (item) => item.workStep,
    summarySchema: OptroWorkStepSummarySchema,
  });
  return (
    <WorkStepOptroImportContext.Provider value={tracker}>
      {children}
    </WorkStepOptroImportContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkStepOptroImport(): WorkStepOptroImportContextValue {
  const value = useContext(WorkStepOptroImportContext);
  if (!value) {
    throw new Error(
      "useWorkStepOptroImport must be used within WorkStepOptroImportProvider",
    );
  }
  return value;
}

// eslint-disable-next-line react-refresh/only-export-components -- For surfaces that can render outside the provider (the sidebar's Background Tasks), which then show no imports.
export function useWorkStepOptroImportIfAvailable(): WorkStepOptroImportContextValue | null {
  return useContext(WorkStepOptroImportContext);
}
